// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const DEMO_PRODUCTS = [
  { id: 1, name: 'Professional Website Package', price: 3500, salePrice: null, sku: 'WEB-PRO-001', category: 'Services', stock: null, status: 'Active', image: '🌐', desc: 'Full custom website design with SEO optimization, mobile responsive, up to 10 pages', sales: 12 },
  { id: 2, name: 'Logo Design Bundle', price: 800, salePrice: 599, sku: 'DES-LOG-001', category: 'Design', stock: null, status: 'Active', image: '🎨', desc: 'Custom logo with 3 concepts, unlimited revisions, all file formats', sales: 24 },
  { id: 3, name: 'Monthly SEO Package', price: 499, salePrice: null, sku: 'SEO-MON-001', category: 'Services', stock: null, status: 'Active', image: '🔍', desc: 'Keyword research, on-page optimization, monthly reporting, backlink building', sales: 8 },
  { id: 4, name: 'Social Media Management', price: 799, salePrice: null, sku: 'SOC-MAN-001', category: 'Services', stock: null, status: 'Active', image: '📱', desc: 'Daily posting, engagement, analytics, 3 platforms included', sales: 15 },
  { id: 5, name: 'Business Card Set (500)', price: 89, salePrice: null, sku: 'PRT-BCD-001', category: 'Print', stock: 200, status: 'Active', image: '🃏', desc: 'Premium matte finish, full color both sides, custom design included', sales: 45 },
  { id: 6, name: 'Brand Strategy Session', price: 1200, salePrice: 999, sku: 'CON-BRD-001', category: 'Consulting', stock: null, status: 'Active', image: '💡', desc: '3-hour deep dive into brand positioning, target audience, messaging', sales: 6 },
  { id: 7, name: 'Email Marketing Setup', price: 350, salePrice: null, sku: 'EML-SET-001', category: 'Services', stock: null, status: 'Draft', image: '📧', desc: 'List setup, first 3 email templates, automation flows', sales: 0 },
  { id: 8, name: 'Video Production (30s)', price: 2500, salePrice: null, sku: 'VID-30S-001', category: 'Media', stock: null, status: 'Active', image: '🎬', desc: 'Professional 30-second ad video with script, filming, editing', sales: 3 },
];

const CATEGORIES = ['All', 'Services', 'Design', 'Print', 'Consulting', 'Media'];

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { fontSize: 14, color: '#999', marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: active ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: active ? '#000' : 'var(--text)' }),
  card: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--border, #2a2a4a)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  stat: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid var(--border, #2a2a4a)' },
  statValue: { fontSize: 28, fontWeight: 800, color: 'var(--accent, #d4a843)' },
  statLabel: { fontSize: 12, color: '#888', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--border, #2a2a4a)', color: '#999', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border, #2a2a4a)', color: 'var(--text)' },
  badge: (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}22`, color }),
  btn: (primary) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: primary ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: primary ? '#000' : 'var(--text)', border: primary ? 'none' : '1px solid var(--border, #2a2a4a)' }),
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
  productCard: { borderRadius: 12, border: '1px solid var(--border, #2a2a4a)', overflow: 'hidden', cursor: 'pointer' },
};

function ProductCatalogPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('catalog');
  const [view, setView] = useState('grid');
  const [filter, setFilter] = useState('All');
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', salePrice: '', sku: '', category: 'Services', desc: '', stock: '', type: 'service' });

  const totalProducts = DEMO_PRODUCTS.length;
  const activeProducts = DEMO_PRODUCTS.filter(p => p.status === 'Active').length;
  const totalRevenue = DEMO_PRODUCTS.reduce((s, p) => s + ((p.salePrice || p.price) * p.sales), 0);
  const totalSales = DEMO_PRODUCTS.reduce((s, p) => s + p.sales, 0);
  const filtered = filter === 'All' ? DEMO_PRODUCTS : DEMO_PRODUCTS.filter(p => p.category === filter);

  const TABS = [
    { id: 'catalog', label: 'Products', icon: '📦' },
    { id: 'orders', label: 'Orders', icon: '🛒' },
    { id: 'storefront', label: 'Storefront', icon: '🏪' },
    { id: 'coupons', label: 'Coupons', icon: '🎟️' },
  ];

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={S.title}>🛍️ Product Catalog</h1>
          <p style={S.subtitle}>Manage your products, services, and online storefront</p>
        </div>
        <button style={S.btn(true)} onClick={() => setCreating(true)}>+ Add Product</button>
      </div>

      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={S.grid}>
        <div style={S.stat}><div style={S.statValue}>{totalProducts}</div><div style={S.statLabel}>Total Products</div></div>
        <div style={S.stat}><div style={S.statValue}>{activeProducts}</div><div style={S.statLabel}>Active</div></div>
        <div style={S.stat}><div style={S.statValue}>${totalRevenue.toLocaleString()}</div><div style={S.statLabel}>Total Revenue</div></div>
        <div style={S.stat}><div style={S.statValue}>{totalSales}</div><div style={S.statLabel}>Total Sales</div></div>
      </div>

      {/* Create Product */}
      {creating && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>Add New Product</div>
            <button style={{ ...S.btn(false), padding: '4px 12px' }} onClick={() => setCreating(false)}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Product Name *</label><input style={S.input} placeholder="Product or service name" value={newProduct.name} onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))} /></div>
            <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>SKU</label><input style={S.input} placeholder="Auto-generated if blank" value={newProduct.sku} onChange={e => setNewProduct(p => ({ ...p, sku: e.target.value }))} /></div>
            <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Price *</label><input style={S.input} type="number" placeholder="0.00" value={newProduct.price} onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))} /></div>
            <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Sale Price</label><input style={S.input} type="number" placeholder="Leave blank for no sale" value={newProduct.salePrice} onChange={e => setNewProduct(p => ({ ...p, salePrice: e.target.value }))} /></div>
            <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Category</label><select style={S.input} value={newProduct.category} onChange={e => setNewProduct(p => ({ ...p, category: e.target.value }))}>{CATEGORIES.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Type</label><select style={S.input} value={newProduct.type} onChange={e => setNewProduct(p => ({ ...p, type: e.target.value }))}><option value="service">Service</option><option value="physical">Physical Product</option><option value="digital">Digital Download</option><option value="subscription">Subscription</option></select></div>
          </div>
          {newProduct.type === 'physical' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Stock Quantity</label>
              <input style={{ ...S.input, maxWidth: 200 }} type="number" placeholder="0" value={newProduct.stock} onChange={e => setNewProduct(p => ({ ...p, stock: e.target.value }))} />
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Description</label>
            <textarea style={{ ...S.input, minHeight: 80, resize: 'vertical' }} placeholder="Product description" value={newProduct.desc} onChange={e => setNewProduct(p => ({ ...p, desc: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Product Image</label>
            <div style={{ padding: 20, borderRadius: 8, border: '2px dashed var(--border, #2a2a4a)', textAlign: 'center', cursor: 'pointer' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>📤</div>
              <div style={{ fontSize: 13, color: '#888' }}>Click to upload or drag & drop</div>
              <div style={{ fontSize: 11, color: '#666' }}>PNG, JPG, WebP up to 5MB</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={S.btn(true)}>💾 Save Product</button>
            <button style={S.btn(false)}>Save as Draft</button>
          </div>
        </div>
      )}

      {/* Catalog Tab */}
      {tab === 'catalog' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {CATEGORIES.map(c => (
                <button key={c} style={{ ...S.tab(filter === c), fontSize: 11, padding: '4px 10px' }} onClick={() => setFilter(c)}>{c}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{ ...S.tab(view === 'grid'), fontSize: 11, padding: '4px 10px' }} onClick={() => setView('grid')}>Grid</button>
              <button style={{ ...S.tab(view === 'list'), fontSize: 11, padding: '4px 10px' }} onClick={() => setView('list')}>List</button>
            </div>
          </div>

          {view === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16 }}>
              {filtered.map(p => (
                <div key={p.id} style={S.productCard}>
                  <div style={{ height: 120, background: 'linear-gradient(135deg, rgba(212, 168, 67, 0.1), rgba(212, 168, 67, 0.05))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>
                    {p.image}
                  </div>
                  <div style={{ padding: 16 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 8, lineHeight: 1.4 }}>{p.desc}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        {p.salePrice ? (
                          <>
                            <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent, #d4a843)' }}>${p.salePrice}</span>
                            <span style={{ fontSize: 13, color: '#888', textDecoration: 'line-through', marginLeft: 8 }}>${p.price}</span>
                          </>
                        ) : (
                          <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent, #d4a843)' }}>${p.price.toLocaleString()}</span>
                        )}
                      </div>
                      <span style={S.badge(p.status === 'Active' ? '#4caf50' : '#888')}>{p.status}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>{p.sales} sales | {p.category}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={S.card}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Product</th>
                    <th style={S.th}>SKU</th>
                    <th style={S.th}>Category</th>
                    <th style={S.th}>Price</th>
                    <th style={S.th}>Stock</th>
                    <th style={S.th}>Sales</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id}>
                      <td style={{ ...S.td, fontWeight: 600 }}><span style={{ marginRight: 8 }}>{p.image}</span>{p.name}</td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11 }}>{p.sku}</td>
                      <td style={S.td}>{p.category}</td>
                      <td style={S.td}>
                        {p.salePrice ? <><span style={{ fontWeight: 700 }}>${p.salePrice}</span> <span style={{ textDecoration: 'line-through', color: '#888', fontSize: 11 }}>${p.price}</span></> : <span style={{ fontWeight: 700 }}>${p.price.toLocaleString()}</span>}
                      </td>
                      <td style={S.td}>{p.stock !== null ? p.stock : '—'}</td>
                      <td style={S.td}>{p.sales}</td>
                      <td style={S.td}><span style={S.badge(p.status === 'Active' ? '#4caf50' : '#888')}>{p.status}</span></td>
                      <td style={S.td}><button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>Edit</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {tab === 'orders' && (
        <div style={S.card}>
          <div style={S.cardTitle}>Recent Orders</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Order #</th>
                <th style={S.th}>Customer</th>
                <th style={S.th}>Items</th>
                <th style={S.th}>Total</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {[
                { id: 'ORD-0124', customer: 'Sarah Johnson', items: 2, total: '$4,099', status: 'Completed', date: 'Feb 18, 2026' },
                { id: 'ORD-0123', customer: 'Mike Chen', items: 1, total: '$499', status: 'In Progress', date: 'Feb 17, 2026' },
                { id: 'ORD-0122', customer: 'Lisa Park', items: 3, total: '$1,687', status: 'Completed', date: 'Feb 15, 2026' },
                { id: 'ORD-0121', customer: 'James Wilson', items: 1, total: '$2,500', status: 'Pending', date: 'Feb 14, 2026' },
                { id: 'ORD-0120', customer: 'Anna Schmidt', items: 2, total: '$888', status: 'Refunded', date: 'Feb 12, 2026' },
              ].map(o => (
                <tr key={o.id}>
                  <td style={{ ...S.td, fontWeight: 600, fontFamily: 'monospace' }}>{o.id}</td>
                  <td style={S.td}>{o.customer}</td>
                  <td style={S.td}>{o.items}</td>
                  <td style={{ ...S.td, fontWeight: 600 }}>{o.total}</td>
                  <td style={S.td}><span style={S.badge(o.status === 'Completed' ? '#4caf50' : o.status === 'In Progress' ? '#2196f3' : o.status === 'Pending' ? '#ff9800' : '#f44336')}>{o.status}</span></td>
                  <td style={S.td}>{o.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Storefront Tab */}
      {tab === 'storefront' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Storefront Settings</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Customize your public-facing product catalog and checkout experience.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Store Name</label><input style={S.input} placeholder="Your Store Name" /></div>
              <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Store URL</label><input style={S.input} placeholder="yourstore.theforgestandard.com" /></div>
            </div>
            <button style={S.btn(true)}>💾 Save</button>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Embed Code</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Add your product catalog to any website with this embed code:</p>
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg, #0f0f23)', fontFamily: 'monospace', fontSize: 12, color: '#4caf50', lineHeight: 1.6, overflowX: 'auto' }}>
              {`<div id="forge-store" data-store="your-store-id"></div>`}<br />
              {`<script src="https://cdn.theforgestandard.com/store.js"></script>`}
            </div>
            <button style={{ ...S.btn(false), marginTop: 12, fontSize: 12 }}>📋 Copy Code</button>
          </div>
        </div>
      )}

      {/* Coupons Tab */}
      {tab === 'coupons' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>Discount Codes</div>
            <button style={S.btn(true)}>+ Create Coupon</button>
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Code</th>
                <th style={S.th}>Discount</th>
                <th style={S.th}>Uses</th>
                <th style={S.th}>Expires</th>
                <th style={S.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                { code: 'WELCOME20', discount: '20% off', uses: '34/100', expires: 'Mar 31, 2026', status: 'Active' },
                { code: 'SPRING50', discount: '$50 off', uses: '12/50', expires: 'Apr 15, 2026', status: 'Active' },
                { code: 'LOYALTY15', discount: '15% off', uses: '8/unlimited', expires: 'None', status: 'Active' },
                { code: 'LAUNCH100', discount: '$100 off', uses: '50/50', expires: 'Feb 28, 2026', status: 'Expired' },
              ].map((c, i) => (
                <tr key={i}>
                  <td style={{ ...S.td, fontWeight: 700, fontFamily: 'monospace' }}>{c.code}</td>
                  <td style={S.td}>{c.discount}</td>
                  <td style={S.td}>{c.uses}</td>
                  <td style={S.td}>{c.expires}</td>
                  <td style={S.td}><span style={S.badge(c.status === 'Active' ? '#4caf50' : '#888')}>{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProductCatalogPage;
